import { Filter } from 'bad-words';

// Initialize profanity filter
const filter = new Filter();

// Reserved usernames that cannot be used (case-insensitive)
const RESERVED_USERNAMES = [
  'admin',
  'administrator',
  'moderator',
  'mod',
  'tribe',
  'howard',
  'bison',
  'hugames',
  'official',
  'support',
  'help',
  'staff',
  'team',
  'system',
  'root',
  'user',
  'guest',
  'owner',
  'webmaster'
];

/**
 * Normalize username for comparison by removing common substitutions
 * This catches l33t speak and similar bypass attempts
 * @param {string} username - Username to normalize
 * @returns {string} - Normalized username
 */
function normalizeUsername(username) {
  return username
    .toLowerCase()
    .replace(/0/g, 'o')
    .replace(/1/g, 'i')
    .replace(/3/g, 'e')
    .replace(/4/g, 'a')
    .replace(/5/g, 's')
    .replace(/7/g, 't')
    .replace(/8/g, 'b')
    .replace(/[@]/g, 'a')
    .replace(/[!]/g, 'i')
    .replace(/[$]/g, 's')
    .replace(/[+]/g, 't')
    // Remove special characters for comparison
    .replace(/[^a-z]/g, '');
}

/**
 * Check if username is reserved
 * @param {string} username - Username to check
 * @returns {boolean} - True if reserved
 */
function isReservedUsername(username) {
  const normalized = normalizeUsername(username);
  return RESERVED_USERNAMES.some(reserved =>
    normalized === reserved || normalized.includes(reserved)
  );
}

/**
 * Validate username content for inappropriate language and reserved names
 * @param {string} username - Username to validate
 * @returns {Object} - { isValid: boolean, reason: string }
 */
export function validateUsernameContent(username) {
  if (!username || typeof username !== 'string') {
    return {
      isValid: false,
      reason: 'Username is required'
    };
  }

  const trimmedUsername = username.trim();

  // Check if username is empty after trimming
  if (trimmedUsername.length === 0) {
    return {
      isValid: false,
      reason: 'Username cannot be empty'
    };
  }

  // Check for reserved usernames
  if (isReservedUsername(trimmedUsername)) {
    return {
      isValid: false,
      reason: 'This username is reserved and cannot be used'
    };
  }

  // Check for profanity in the original username
  if (filter.isProfane(trimmedUsername)) {
    return {
      isValid: false,
      reason: 'Username contains inappropriate content. Please choose another.'
    };
  }

  // Check normalized version to catch l33t speak and substitutions
  const normalized = normalizeUsername(trimmedUsername);
  if (filter.isProfane(normalized)) {
    return {
      isValid: false,
      reason: 'Username contains inappropriate content. Please choose another.'
    };
  }

  // All checks passed
  return {
    isValid: true,
    reason: ''
  };
}

/**
 * Get list of reserved usernames (for documentation/display purposes)
 * @returns {Array<string>} - List of reserved usernames
 */
export function getReservedUsernames() {
  return [...RESERVED_USERNAMES];
}
